/**
 * Puesto nuevo.
 *
 * Pensado para el caso real: llega alguien, se sienta en la barra, y el mesero
 * necesita tomarle el pedido ya. No hay pantalla de "configurar mesas" de por
 * medio — se escribe un nombre y se abre la cuenta en el mismo paso.
 *
 * Por defecto el puesto es **temporal**: el backend lo archiva solo cuando se
 * cobra su última cuenta, así la grilla no se llena de "Barra 1", "Barra 2",
 * "Barra 3" de días pasados. Si el puesto vino para quedarse —una mesa nueva
 * del salón— se desmarca y queda fijo.
 */

import { useState } from 'react';

interface Props {
  onCrear: (datos: {
    nombre: string;
    capacidad: number;
    esTemporal: boolean;
    etiqueta: string | null;
  }) => void;
  onCerrar: () => void;
}

const SUGERENCIAS = ['Barra', 'Para llevar', 'Terraza', 'Domicilio'];

export function ModalPuestoNuevo({ onCrear, onCerrar }: Props) {
  const [nombre, setNombre] = useState('');
  const [capacidad, setCapacidad] = useState('2');
  const [esTemporal, setEsTemporal] = useState(true);
  const [etiqueta, setEtiqueta] = useState('');

  const crear = () => {
    const limpio = nombre.trim();
    if (!limpio) return;
    onCrear({
      nombre: limpio,
      capacidad: Math.min(30, Math.max(1, Number(capacidad) || 1)),
      esTemporal,
      etiqueta: etiqueta.trim() || null,
    });
  };

  return (
    <div className="overlay on" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal sm" role="dialog" aria-modal="true">
        <header>
          <h2>Abrir puesto</h2>
          <button className="close" onClick={onCerrar} aria-label="Cerrar">
            ✕
          </button>
        </header>

        <div className="body">
          <div className="field">
            <label htmlFor="nombrePuesto">¿Dónde se sentaron?</label>
            <input
              id="nombrePuesto"
              autoFocus
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. Barra"
              onKeyDown={(e) => e.key === 'Enter' && crear()}
            />
          </div>

          <div className="chips" style={{ marginTop: -4 }}>
            {SUGERENCIAS.map((s) => (
              <button
                key={s}
                className="chip"
                aria-pressed={nombre === s}
                onClick={() => setNombre(s)}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="field">
            <label htmlFor="capPuesto">Comensales</label>
            <input
              id="capPuesto"
              type="number"
              min={1}
              max={30}
              value={capacidad}
              onChange={(e) => setCapacidad(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="etiquetaPuesto">Nombre de la cuenta (opcional)</label>
            <input
              id="etiquetaPuesto"
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
              placeholder="Ej. Señor del café"
            />
          </div>

          <label className="tip" style={{ marginTop: 4 }}>
            <input
              type="checkbox"
              checked={esTemporal}
              onChange={(e) => setEsTemporal(e.target.checked)}
            />
            <span className="switch" />
            <span style={{ fontSize: 12.5 }}>
              Quitar de la grilla al cobrar
              <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 11 }}>
                Desmarcalo si es una mesa que llegó para quedarse
              </span>
            </span>
          </label>
        </div>

        <footer>
          <button className="btn btn-ghost" onClick={onCerrar}>
            Cancelar
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" disabled={!nombre.trim()} onClick={crear}>
            Abrir y tomar pedido
          </button>
        </footer>
      </div>
    </div>
  );
}
